// rc121 — Tarjeta WiFi (SSID + clave + QR) para conectar el módem del cliente.
// Portada 1:1 de byteacs/web/src/lib/wifiCard.ts — misma imagen que imprime
// ByteACS, ahora generable desde la ficha del contrato en ByteISP.
import QRCode from "qrcode";

export function wifiQrPayload(ssid: string, password: string): string {
  const esc = (s: string) => s.replace(/([\\;,:"])/g, "\\$1");
  return `WIFI:T:WPA;S:${esc(ssid)};P:${esc(password)};;`;
}

// Renderiza la tarjeta a un PNG (Blob). Si no hay password (equipo con clave de
// fábrica), dibuja la nota de "clave en la etiqueta" y omite el QR (un QR sin
// clave no sirve para autoconectar).
export async function renderWifiCard(
  ssid: string,
  password: string,
  band: string,
  cliente: string,
  marca = "",
): Promise<Blob> {
  // rc129 — la marca viene del caller (empresa del cliente / nombre comercial
  // del ISP); antes "BYTECOM" fijo.
  const MARCA = (marca || "").trim().toUpperCase();
  const S = 2; // escala retina
  const W = 800, H = 1150;
  const cv = document.createElement("canvas");
  cv.width = W * S;
  cv.height = H * S;
  const ctx = cv.getContext("2d")!;
  ctx.scale(S, S);

  const rr = (x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  // fondo
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#17142a");
  bg.addColorStop(1, "#0b0a12");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  // glow
  const glow = ctx.createRadialGradient(W / 2, 150, 20, W / 2, 150, 420);
  glow.addColorStop(0, "rgba(124,108,240,0.22)");
  glow.addColorStop(1, "rgba(124,108,240,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // header: logo + marca
  const lg = ctx.createLinearGradient(60, 60, 116, 116);
  lg.addColorStop(0, "#a78bfa");
  lg.addColorStop(1, "#4a9eff");
  ctx.fillStyle = lg;
  rr(60, 60, 56, 56, 16);
  ctx.fill();
  ctx.fillStyle = "#0d0c14";
  ctx.font = "800 30px Inter, sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText("m", 88, 90);
  ctx.textAlign = "left";
  ctx.fillStyle = "#eae7f2";
  ctx.font = "800 22px Inter, sans-serif";
  ctx.fillText("mi módem", 130, 80);
  ctx.fillStyle = "#8b84a0";
  ctx.font = '700 12px "JetBrains Mono", monospace';
  ctx.fillText(MARCA || "WIFI", 130, 102);

  // título
  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 34px Inter, sans-serif";
  ctx.fillText("Conéctate a mi WiFi", W / 2, 190);
  ctx.fillStyle = "#9a93ad";
  ctx.font = "500 16px Inter, sans-serif";
  ctx.fillText(
    password ? "Escanea el código con la cámara de tu teléfono" : "La clave está en la etiqueta del equipo",
    W / 2, 224,
  );

  // panel blanco con el QR (solo si hay clave)
  const panelW = 500, panelX = (W - panelW) / 2, panelY = 262;
  if (password) {
    const qrDataUrl = await QRCode.toDataURL(wifiQrPayload(ssid, password), {
      margin: 1,
      width: 460,
      errorCorrectionLevel: "M",
      color: { dark: "#0d0c14", light: "#ffffff" },
    });
    const qrImg = await new Promise<HTMLImageElement>((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = qrDataUrl;
    });
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.45)";
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 16;
    ctx.fillStyle = "#ffffff";
    rr(panelX, panelY, panelW, panelW, 28);
    ctx.fill();
    ctx.restore();
    ctx.drawImage(qrImg, panelX + 20, panelY + 20, panelW - 40, panelW - 40);
  } else {
    // placeholder sin QR
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    rr(panelX, panelY, panelW, panelW, 28);
    ctx.fill();
    ctx.fillStyle = "#6b6482";
    ctx.font = "600 18px Inter, sans-serif";
    ctx.fillText("sin QR — clave de fábrica", W / 2, panelY + panelW / 2);
  }

  // banda pill
  const pillY = panelY + panelW + 34;
  ctx.fillStyle = "rgba(167,149,214,0.14)";
  const pillW = 120, pillX = (W - pillW) / 2;
  rr(pillX, pillY, pillW, 30, 15);
  ctx.fill();
  ctx.fillStyle = "#a795d6";
  ctx.font = '700 13px "JetBrains Mono", monospace';
  ctx.textAlign = "center";
  ctx.fillText((band || "WiFi").toUpperCase(), W / 2, pillY + 15);

  // datos: nombre + contraseña
  const dataY = pillY + 70;
  ctx.fillStyle = "#8b84a0";
  ctx.font = '700 13px "JetBrains Mono", monospace';
  ctx.fillText("NOMBRE DE LA RED", W / 2, dataY);
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 30px Inter, sans-serif";
  ctx.fillText(ssid, W / 2, dataY + 36);

  ctx.fillStyle = "#8b84a0";
  ctx.font = '700 13px "JetBrains Mono", monospace';
  ctx.fillText("CONTRASEÑA", W / 2, dataY + 92);
  ctx.fillStyle = "#a8b8ff";
  ctx.font = '800 30px "JetBrains Mono", monospace';
  ctx.fillText(password || "— (etiqueta del equipo) —", W / 2, dataY + 128);

  // footer
  ctx.fillStyle = "#5f5975";
  ctx.font = "500 14px Inter, sans-serif";
  ctx.fillText(
    cliente ? (MARCA ? `${cliente} · ${MARCA}` : cliente) : (MARCA ? `${MARCA} · Internet` : "Internet"),
    W / 2, H - 46,
  );

  return await new Promise<Blob>((res) => cv.toBlob((b) => res(b!), "image/png", 0.95));
}

// Clave WiFi legible (sin caracteres ambiguos), 12 chars — misma de ByteACS.
export function genWifiPass(): string {
  const abc = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const a = new Uint32Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (x) => abc[x % abc.length]).join("");
}
